import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { apiClient } from "../Api/AllApi";
import velanLogo from "../../Frontend/Images/velan-plastic-logo.jpg";

const OwedReceipt = () => {
  const { id, paymentId } = useParams();
  const [receiptData, setReceiptData] = useState(null);

  useEffect(() => {
    // Fetch the payment receipt data using customer id and payment id
    const fetchPaymentReceipt = async () => {
      try {
        const res = await apiClient.get(
          `/customers/${id}/payment-receipt/${paymentId}`
        );
        const data = res.data;
        setReceiptData({
          customerName: data.customer_name,
          oldOwed: data.old_owed,
          payment: data.payment,
          totalOwed: data.total_remaind,
          sender: data.sender,
          receiver: data.receiver,
          date: data.created_at,
        });
      } catch (error) {
        setReceiptData(null);
        console.error("Error fetching payment receipt:", error);
      }
    };
    if (id && paymentId) fetchPaymentReceipt();
  }, [id, paymentId]);

  const getBase64Logo = () => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = velanLogo; // Imported logo path
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = (err) => {
        console.error("Image load failed:", err);
        reject(err);
      };
    });
  };

  const handlePrint = async () => {
    const printContent = document.querySelector(".printable-content");

    try {
      const base64Logo = await getBase64Logo();

      const clonedContent = printContent.cloneNode(true);

      const img = clonedContent.querySelector("img");
      if (img) {
        img.setAttribute("src", base64Logo);
      }

      const printWindow = window.open("", "_blank");

      printWindow.document.write(`
        <html dir="rtl" lang="ar">
          <head>
            <title>Payment Receipt</title>
            <meta charset="UTF-8" />
            <style>
              body {
                font-family: 'Cairo', Arial, sans-serif;
                direction: rtl;
                background: #fff;
                color: #000;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                border: 1px solid #ddd;
                text-align: right;
              }
              .printable-content {
                width: 100%;
                max-width: 19cm;
                margin: auto;
                box-sizing: border-box;
              }
            </style>
          </head>
          <body>
            <div class="printable-content">
              ${clonedContent.innerHTML}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      // Ensure the image loads before printing
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
        window.location.reload();
      };
    } catch (err) {
      console.error("Error during print setup:", err);
    }
  };

  return (
    <>
      {/* Loading and error states */}
      {!receiptData && (
        <div
          style={{
            textAlign: "center",
            color: "red",
            fontWeight: "bold",
            margin: "2rem",
          }}
        >
          لا توجد بيانات للإيصال أو لم يتم العثور على الدفعة.
        </div>
      )}
      {receiptData && (
        <div>
          <div
            className="printable-content"
            style={{
              fontFamily: "Cairo, Arial, sans-serif",
              direction: "rtl",
              width: "100%",
              maxWidth: "19cm",
              minHeight: "27cm",
              margin: "1.5cm auto",
              padding: "1cm",
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              position: "relative",
              boxSizing: "border-box",
            }}
          >
            {/* Receipt Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "2.5rem",
                borderBottom: "2px solid #222",
                paddingBottom: "1.2rem",
                zIndex: 2,
                position: "relative",
              }}
            >
              <div
                style={{
                  color: "#111",
                  fontWeight: "bold",
                  fontSize: "2.4rem",
                  letterSpacing: "2px",
                  textShadow: "0 2px 8px rgba(0,0,0,0.10)",
                  fontFamily: "Cairo, Arial, sans-serif",
                }}
              >
                معمل ڤیلان
                <div
                  style={{
                    fontSize: "1.1rem",
                    color: "#444",
                    fontWeight: 400,
                    marginTop: 4,
                  }}
                ></div>
              </div>
              <div style={{ textAlign: "right" }}>
                <img
                  src={velanLogo}
                  alt="Logo"
                  style={{
                    width: "180px", // Make logo bigger
                    height: "auto",
                    background: "#fff",
                  }}
                />
              </div>
            </div>
            {/* Receipt Number (moved below header) */}
            <div
              style={{
                textAlign: "center",
                fontSize: "1.2rem",
                color: "#222",
                fontWeight: 600,
                margin: "0 0 1rem 0", // Adjusted margin to move it higher
                letterSpacing: "1px",
              }}
            >
              رقم الإيصال:{" "}
              <span style={{ color: "#111", fontWeight: 700 }}>
                {paymentId}
              </span>
            </div>
            {/* Receipt Title */}
            <h3
              style={{
                textAlign: "center",
                fontSize: "2rem", // Slightly reduced font size
                color: "#111",
                marginBottom: "2rem", // Adjusted margin to move it higher
                fontWeight: "bold",
                letterSpacing: "1px",
                background: "#f7f7f7",
                borderRadius: "8px",
                border: "1.5px solid #bbb",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                width: "60%", // Slightly reduced width
                margin: "0 auto 2rem auto", // Adjusted margin
                zIndex: 2,
                position: "relative",
              }}
            >
              سند قبض
            </h3>
            {/* Receipt Body */}
            <div
              style={{
                fontSize: "1rem", // Reduced font size
                lineHeight: "1.8", // Adjusted line height
                padding: "1rem 1rem 1rem 1rem", // Reduced padding
                marginBottom: "2rem", // Adjusted margin
                background: "#fff",
                borderRadius: "12px", // Reduced border radius
                boxShadow: "0 1px 10px rgba(0,0,0,0.08)", // Reduced shadow
                border: "1px solid #ddd", // Adjusted border thickness
                zIndex: 2,
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "190px 1fr",
                  gap: "1.3rem 2.7rem",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  اسم الزبون:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {receiptData?.customerName}
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  تاريخ ووقت الدفع:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {receiptData?.date
                    ? format(new Date(receiptData.date), "dd/MM/yyyy HH:mm")
                    : ""}
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  القرض السابق:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {Number(receiptData?.oldOwed).toLocaleString()}{" "}
                  <span style={{ color: "#111", fontWeight: 500 }}>IQD</span>
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  مبلغ الدفع:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {Number(receiptData?.payment).toLocaleString()}{" "}
                  <span style={{ color: "#111", fontWeight: 500 }}>IQD</span>
                </span>
                <span style={{ color: "#111", fontWeight: "bold" }}>
                  القرض الكلي:
                </span>
                <span style={{ color: "#222", fontWeight: "bold" }}>
                  {Number(receiptData?.totalOwed).toLocaleString()}{" "}
                  <span style={{ color: "#111", fontWeight: 500 }}>IQD</span>
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "2.5rem",
                  paddingTop: "1.2rem",
                  borderTop: "1px dashed #bbb",
                }}
              >
                <div>
                  <span
                    style={{
                      color: "#111",
                      fontWeight: "bold",
                      marginLeft: "8px",
                    }}
                  >
                    المستلم :
                  </span>
                  <span style={{ color: "#222", fontWeight: "bold" }}>
                    {receiptData?.receiver}
                  </span>
                </div>
                <div>
                  <span
                    style={{
                      color: "#111",
                      fontWeight: "bold",
                      marginLeft: "8px",
                    }}
                  >
                    المسلم :
                  </span>
                  <span style={{ color: "#222", fontWeight: "bold" }}>
                    {receiptData?.sender}
                  </span>
                </div>
              </div>
            </div>
            {/* Signature Areas */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                margin: "2.5rem 0 1.5rem 0",
                zIndex: 2,
                position: "relative",
              }}
            >
              <div style={{ textAlign: "center", width: "40%" }}>
                <div
                  style={{
                    borderBottom: "2px dotted #111",
                    height: 40,
                    marginBottom: 8,
                  }}
                ></div>
                <span style={{ color: "#111", fontWeight: 500 }}>
                  توقيع المستلم
                </span>
              </div>
              <div style={{ textAlign: "center", width: "40%" }}>
                <div
                  style={{
                    borderBottom: "2px dotted #111",
                    height: 40,
                    marginBottom: 8,
                  }}
                ></div>
                <span style={{ color: "#111", fontWeight: 500 }}>
                  توقيع المسلم
                </span>
              </div>
            </div>
            {/* Footer Info */}
            <div
              style={{
                position: "absolute",
                bottom: "15px",
                left: 1,
                right: 1,
                background: "#f7f7f7",
                borderRadius: "10px 10px 0 0",
                padding: "1.5rem 2rem 1.2rem 2rem",
                boxShadow: "0 -2px 10px rgba(0,0,0,0.06)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "1.05rem",
                zIndex: 2,
              }}
            >
              {/* RIGHT: Location */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  textAlign: "right",
                }}
              >
                <span style={{ color: "#222", fontSize: 14 }}>
                  عنوان: عراق-اربيل طريق مخمور مامزاوا صناعة تيمار{" "}
                </span>
              </div>

              {/* MIDDLE: Phone numbers */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span style={{ color: "#222", fontSize: 14 }}>
                    {" "}
                    7566 456 0750{" "}
                  </span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span style={{ color: "#222", fontSize: 14 }}>
                    {" "}
                    1022 234 0770
                  </span>
                </div>
              </div>

              {/* LEFT: Email + Website */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span style={{ color: "#222", fontSize: 14 }}>
                    Email: info@velan-plastic.com
                  </span>
                </div>
                <div
                  style={{ display: "flex", alignItems: "left", gap: "6px" }}
                >
                  <span style={{ color: "#222", fontSize: 14 }}>
                    Web:http://velan-plastic.com
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              position: "fixed",
              bottom: "40px",
              right: "40px",
              zIndex: 1000,
            }}
            className="no-print"
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              style={{ marginBottom: "10px" }}
            >
              طباعة
            </Button>
          </div>
          <style>
            {`
              @media print {
                @page {
                  size: A4;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                  background: #f4f8fb; // Match on-screen background
                  direction: rtl; // Ensure right-to-left layout
                  font-family: "Cairo, Arial, sans-serif"; // Match on-screen font
                }
                .printable-content {
                  width: 19cm; // Match on-screen width
                  min-height: 27.7cm; // Match on-screen height
                  padding: 2cm 1.5cm 2.5cm 1.5cm; // Match on-screen padding
                  background: #f4f8fb; // Match on-screen background
                  box-shadow: none; // Remove shadow for printing
                  border-radius: 0; // Remove border radius for printing
                  border: 1px solid #3498db; // Match on-screen border
                }
                .no-print {
                  display: none !important;
                }
                .MuiButton-root {
                  display: none !important;
                }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

export default OwedReceipt;
